//////////////////
// STRING INPUTS
//////////////////

import React, {useEffect, useState} from "react";
import {
    convertMetricKeyToSlug,
    convertMetricSlugToKey,
    convertStringToMoment,
    getOptionGroup
} from "@components/Chatbot/PlanEditor/utils";
import {Button, Checkbox, DatePicker, InputNumber, Select, Tooltip} from "antd";
import dayjs from "dayjs";
import {FaXmark} from "react-icons/fa6";
import {useSelector} from "react-redux";
import {OptionGroupSelect} from "@components/Compare/Components_1v1/OptionGroupSelect";
import { useDataAccess } from "@components/PermissionedComponents";
import {MdInfoOutline} from "react-icons/md";
import {DeleteOutlined} from "@ant-design/icons";
import {IoAddCircle} from "react-icons/io5";

const {RangePicker} = DatePicker;


///////////////
// UTILS
///////////////


const NullDisplay = ({message}) => {
    return (
        <p className="string-multi-input-null-display">
            {message}
        </p>
    )
}


/////////////////
// METRIC INPUTS
/////////////////

export const MetricSingleInput = ({label, value, onChange, functionConfig, parameterConfig}) => {
    const {
        all_metric_types,
    } = useSelector(({team}) => team);
    const { canAccess, ready: permissionsReady } = useDataAccess();
    const [metricOptions, setMetricOptions] = useState([]);

    useEffect(() => {
        let groupOptions = [];
        for (const [metric_type, metrics] of Object.entries(all_metric_types)) {
            if (!functionConfig?.metric_types?.includes(metric_type)) {
                continue;
            }
            // A generated plan must not reference a stream the user cannot read.
            if (!canAccess(metric_type)) {
                continue;
            }
            const metricsFiltered = metrics?.filter((e) => e.visible === true || e.visible === undefined)
            if (metricsFiltered.length > 0) {
                let groupLabel;
                if (metric_type === "gps") groupLabel = "GPS";
                else groupLabel = metric_type.charAt(0).toUpperCase() + metric_type.slice(1).toLowerCase(); //Capitalize
                groupOptions.push(getOptionGroup(groupLabel, metricsFiltered, metric_type));
            }
        }

        setMetricOptions(groupOptions)
    }, [all_metric_types, permissionsReady])

    const convertMetricSlugsToKeys = (slug) => {
        return [convertMetricSlugToKey(slug, all_metric_types, functionConfig)];
    }

    const handleChange = (value) => {
        if (!value.length) return;
        const newMetric = value[1];
        const newMetricSlug = convertMetricKeyToSlug(newMetric, all_metric_types, functionConfig)
        onChange(newMetricSlug)
    }

    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">
                {label}:
            </label>
            <OptionGroupSelect
                selectedOptionVal={convertMetricSlugsToKeys(value)}
                options={metricOptions}
                placeholder="Select Metric"
                handleOptionGroupChange={handleChange}
            />
        </div>
    )
}


export const MetricMultiInput = ({label, value, onChange, functionConfig, parameterConfig}) => {
    const {
        all_metric_types,
    } = useSelector(({team}) => team);
    const { canAccess, ready: permissionsReady } = useDataAccess();
    const [metricOptions, setMetricOptions] = useState([]);

    useEffect(() => {
        let groupOptions = [];
        for (const [metric_type, metrics] of Object.entries(all_metric_types)) {
            if (!functionConfig?.metric_types?.includes(metric_type)) {
                continue;
            }
            // A generated plan must not reference a stream the user cannot read.
            if (!canAccess(metric_type)) {
                continue;
            }
            const metricsFiltered = metrics?.filter((e) => e.visible === true || e.visible === undefined)
            if (metricsFiltered.length > 0) {
                let groupLabel;
                if (metric_type === "gps") groupLabel = "GPS";
                else groupLabel = metric_type.charAt(0).toUpperCase() + metric_type.slice(1).toLowerCase(); //Capitalize
                groupOptions.push(getOptionGroup(groupLabel, metricsFiltered, metric_type));
            }
        }

        setMetricOptions(groupOptions)
    }, [all_metric_types, permissionsReady])

    const convertMetricSlugsToKeys = (slugs) => {
        return slugs.map(f => convertMetricSlugToKey(f, all_metric_types, functionConfig))
    }

    const handleChange = (value) => {
        const newMetricSlugs = value.map(k => convertMetricKeyToSlug(k, all_metric_types, functionConfig))
        onChange(newMetricSlugs);
    }

    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <OptionGroupSelect
                selectedOptionVal={convertMetricSlugsToKeys(value)}
                options={metricOptions}
                placeholder="Select Metric(s)"
                handleOptionGroupChange={handleChange}
            />
        </div>
    )
}

////////////////////////////
// PLAYER POS GROUP INPUTS
////////////////////////////


export const PlayerSingleInput = ({label, value, onChange, functionConfig, parameterConfig}) => {
    const {
        allPlayers,
    } = useSelector(({team}) => team);
    const [playerOptions, setPlayerOptions] = useState([]);

    useEffect(() => {
        let groupOptions = [];
        if (allPlayers.length > 0) {
            groupOptions.push(getOptionGroup("Players", allPlayers, "player"));
        }

        setPlayerOptions(groupOptions)
    }, [allPlayers])

    const convertPlayerToIdentifier = (playerName) => {
        const player = allPlayers.find(
            (player) => `${player['first_name']} ${player['last_name']}` === playerName
        );

        return player ? `player-${player['player_id']}` : null;
    }

    const handleChange = (value) => {
        if (!value.length) return;
        const newPlayer = value[1]
        const player = allPlayers.find(
            (player) => `player-${player.player_id}` === newPlayer
        );
        const playerName = `${player.first_name} ${player.last_name}`
        onChange(playerName)
    }

    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <OptionGroupSelect
                selectedOptionVal={[convertPlayerToIdentifier(value)]}
                options={playerOptions}
                placeholder="Select Player"
                handleOptionGroupChange={handleChange}
            />
        </div>
    )
}


export const PlayerMultiInput = ({label, value, onChange, functionConfig, parameterConfig}) => {
    const {
        allPlayers,
    } = useSelector(({team}) => team);
    const [playerOptions, setPlayerOptions] = useState([]);

    useEffect(() => {
        let groupOptions = [];
        if (allPlayers.length > 0) {
            groupOptions.push(getOptionGroup("Players", allPlayers, "player"));
        }

        setPlayerOptions(groupOptions)
    }, [allPlayers])

    const convertPlayersToIdentifiers = (playerNames) => {
        return playerNames.map(playerName => {
            const player = allPlayers.find(
                (player) => `${player['first_name']} ${player['last_name']}` === playerName
            );

            return player ? `player-${player['player_id']}` : null;
        }).filter(pn => !!pn)
    }

    const handleChange = (value) => {
        const playerNames = value.map(pid => {
            const player = allPlayers.find(
                (player) => `player-${player.player_id}` === pid
            );
            return `${player.first_name} ${player.last_name}`
        })
        onChange(playerNames);
    }

    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <OptionGroupSelect
                selectedOptionVal={convertPlayersToIdentifiers(value)}
                options={playerOptions}
                placeholder="Choose Player(s)"
                handleOptionGroupChange={handleChange}
            />
        </div>
    )
}


export const PositionsSingleInput = ({label, value, onChange, functionConfig, parameterConfig}) => {
    const {
        positions,
    } = useSelector(({team}) => team);
    const [posOptions, setPosOptions] = useState([]);

    useEffect(() => {
        let groupOptions = [];
        if (positions.length > 0) {
            groupOptions.push(getOptionGroup("Positions", positions, "pos"));
        }

        setPosOptions(groupOptions)
    }, [positions])

    const convertPosToIdentifier = (value) => {
        return value.map(p => {
            const position = positions.find(
                (pos) => pos === p
            );

            return position ? `pos-${position}` : null;
        }).filter(pn => !!pn)
    }

    const handleChange = (value) => {
        if (value.length <= 1) {
            const newValue = value.map(v => v.slice(4));
            onChange(newValue);
            return;
        }
        const newValue = value[1]
        const position = positions.find(v => `pos-${v}` === newValue);
        onChange(position ? [position] : []);
    }

    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <OptionGroupSelect
                selectedOptionVal={convertPosToIdentifier(value)}
                options={posOptions}
                placeholder="Select Position"
                handleOptionGroupChange={handleChange}
            />
        </div>
    )
}


export const PositionsMultiInput = ({label, value, onChange, functionConfig, parameterConfig}) => {
    const {
        positions,
    } = useSelector(({team}) => team);
    const [posOptions, setPosOptions] = useState([]);

    useEffect(() => {
        let groupOptions = [];
        if (positions.length > 0) {
            groupOptions.push(getOptionGroup("Positions", positions, "pos"));
        }

        setPosOptions(groupOptions)
    }, [positions])

    const convertPosToIdentifier = (value) => {
        return value.map(pos => {
            const position = positions.find(p => pos === p)
            return position ? `pos-${position}` : null
        }).filter(p => !!p).sort();
    }

    const handleChange = (value) => {
        if (!value.length) {
            onChange(value);
            return;
        }
        ;
        const newValues = value.map(pos => {
            const position = positions.find(p => `pos-${p}` === pos)
            return position ? position : null
        }).filter(p => !!p);
        onChange(newValues);
    }

    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <OptionGroupSelect
                selectedOptionVal={convertPosToIdentifier(value)}
                options={posOptions}
                placeholder="Select Position"
                handleOptionGroupChange={handleChange}
            />
        </div>
    )
}


export const PlayerGroupsMultiInput = ({label, value, onChange, functionConfig, parameterConfig}) => {
    const {
        playerGroups: groups,
    } = useSelector(({team}) => team);
    const [playergroupOptions, setPlayergroupOptions] = useState([]);

    useEffect(() => {
        let groupOptions = [];
        if (groups.length > 0) {
            groupOptions.push(getOptionGroup("Groups", groups, "group"));
        }

        setPlayergroupOptions(groupOptions)
    }, [groups])

    const convertPosToIdentifier = (value) => {
        return value.map(e => {
            const group = groups.find(
                g => g.name === e
            );

            return group ? `group-${group?.player_group_id}` : null;
        }).filter(pn => !!pn)
    }

    const handleChange = (value) => {
        if (!value.length) {
            onChange(value);
            return;
        }
        ;
        const newValues = value.map(pos => {
            const group = groups.find(g => `group-${g?.player_group_id}` === pos)
            return group ? group?.name : null;
        }).filter(p => !!p);
        onChange(newValues);
    }

    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <OptionGroupSelect
                selectedOptionVal={convertPosToIdentifier(value)}
                options={playergroupOptions}
                placeholder="Select Player Group"
                handleOptionGroupChange={handleChange}
            />
        </div>
    )
}


/////////////////
// STRING INPUTS
/////////////////

export const StringInput = ({label, value, onChange, type = "text", parameterConfig}) => {
    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <input
                type={type}
                className="chatbot-editable-action-param-value-input"
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    )
}


export const StringListInput = ({label, value, onChange, parameterConfig}) => {
    const handleChange = (newValue, index) => {
        const newList = value.map((v, i) => i === index ? newValue : v);
        onChange(newList);
    }

    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <div className={"chatbot-editable-action-multi-input"}>
                {
                    value.length === 0 && (
                        <NullDisplay message={"None selected."}/>
                    )
                }
                {value.map((val, index) => (
                    <div className={"chatbot-editable-action-multi-input-row"} key={index}>
                        <input
                            key={index}
                            type="text"
                            className="chatbot-editable-action-param-value-input"
                            value={val}
                            onChange={(e) => handleChange(e.target.value, index)}
                            style={{width: '90%', maxWidth: 360}}
                        />
                        {/* Button to remove item */}
                        <div
                            onClick={() => {
                                const newValue = value.filter((_, i) => i !== index);
                                onChange(newValue);
                            }}
                        >
                            <FaXmark
                                style={{color: "red", marginBottom: "6px"}}
                            />
                        </div>
                    </div>
                ))}
                {/* Button to add new item */}
                <IoAddCircle
                    size={"lg"}
                    color={"#1890ff"}
                    className="chatbot-editable-action-param-row-button"
                    onClick={() => {
                        const newList = [...value, ""];
                        onChange(newList);
                    }}
                />
            </div>
        </div>
    )
}


export const StringSelectionInput = ({label, value, onChange, options, parameterConfig}) => {
    const getOptions = () => {
        return options.map(o => ({
            value: o,
            label: o
        }))
    }

    const handleChange = (newValue) => {
        onChange(newValue);
    }

    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <div className={"chatbot-editable-action-multi-input"}>
                <Select
                    defaultValue={value}
                    style={{width: 120}}
                    options={getOptions()}
                    onChange={handleChange}
                />
            </div>
        </div>
    )
}

////////////////////////
// DATES / DATE RANGES
////////////////////////

export const DateRangeInput = ({label, dateRange, onChange, parameterConfig}) => {
    const parseDateRange = (dateRange) => {
        if (!dateRange || dateRange.length !== 2) {
            return [null, null];
        }
        const [startDate, endDate] = dateRange;
        return [startDate || null, endDate || null].map(convertStringToMoment);
    }

    const handleChange = (newDateRange) => {
        const [startMoment, endMoment] = newDateRange;
        const startDate = startMoment ? startMoment.format('YYYY-MM-DD') : null;
        const endDate = endMoment ? endMoment.format('YYYY-MM-DD') : null;
        onChange([startDate, endDate]);
    }

    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <RangePicker
                onChange={handleChange}
                value={parseDateRange(dateRange)}
                style={{width: '70%', maxWidth: 240}}
            />
        </div>
    )
}

export const DateSingleInput = ({label, date, onChange, parameterConfig}) => {
    const handleChange = (newDate) => {
        const date = newDate ? newDate.format('YYYY-MM-DD') : null;
        onChange(date);
    }

    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <DatePicker
                onChange={handleChange}
                value={dayjs(date)}
                style={{width: '40%', maxWidth: 160}}
            />
        </div>
    )
}

export const DateMultiInput = ({label, dates, onChange, parameterConfig}) => {
    const handleChange = (newDate, index) => {
        const dateStr = newDate ? newDate.format('YYYY-MM-DD') : null;
        const newDates = dates.map((d, i) => i === index ? dateStr : d);
        onChange(newDates);
    }

    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <div className={"chatbot-editable-action-multi-input"}>
                <div className={"chatbot-editable-action-multi-input-section"}>
                    {
                        dates.length === 0 && (
                            <NullDisplay message={"No dates selected."}/>
                        )
                    }
                    {dates.map((date, index) => (
                        <div className={"chatbot-editable-action-multi-input-row"} key={index}>
                            <DatePicker
                                key={index}
                                onChange={(newDate) => handleChange(newDate, index)}
                                value={convertStringToMoment(date)}
                                style={{width: '90%', maxWidth: 360}}
                            />
                            {/* Button to remove date */}
                            <div
                                onClick={() => {
                                    const newDates = dates.filter((_, i) => i !== index);
                                    onChange(newDates);
                                }}
                            >
                                <FaXmark
                                    style={{
                                        color: "red",
                                        marginBottom: "6px",
                                        cursor: "pointer"
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <div
                    className={"chatbot-editable-action-multi-input-section"}
                    style={{
                        justifyContent: "flex-end",
                        alignItems: "center",
                    }}
                >
                    {/* Button to add new date */}
                    <IoAddCircle
                        size={"lg"}
                        color={"#1890ff"}
                        className="chatbot-editable-action-param-row-button"
                        onClick={() => {
                            const newDates = [...dates, null];
                            onChange(newDates);
                        }}
                    />
                </div>
            </div>
        </div>
    )
}

export const DateRangeMultiInput = ({label, dateRanges, onChange, parameterConfig}) => {
    const handleChange = (newDateRange, index) => {
        const [startMoment, endMoment] = newDateRange;
        const startDate = startMoment ? startMoment.format('YYYY-MM-DD') : null;
        const endDate = endMoment ? endMoment.format('YYYY-MM-DD') : null;
        const newRanges = dateRanges.map((range, i) => i === index ? [startDate, endDate] : range);
        onChange(newRanges);
    }

    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <div className={"chatbot-editable-action-multi-input"}>
                <div className={"chatbot-editable-action-multi-input-section"}>
                    {
                        dateRanges.length === 0 && (
                            <NullDisplay message={"No date ranges selected."}/>
                        )
                    }
                    {dateRanges.map((dateRange, index) => (
                        <div className={"chatbot-editable-action-multi-input-row"} key={index}>
                            <RangePicker
                                key={index}
                                onChange={(newDateRange) => handleChange(newDateRange, index)}
                                value={[
                                    convertStringToMoment(dateRange[0]),
                                    convertStringToMoment(dateRange[1])
                                ]}
                                style={{width: '90%', maxWidth: 360}}
                            />
                            {/* Button to remove date range */}
                            <div
                                onClick={() => {
                                    const newDates = dateRanges.filter((_, i) => i !== index);
                                    onChange(newDates);
                                }}
                            >
                                <Button
                                    icon={<DeleteOutlined />}
                                    shape="circle"
                                    danger
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <div
                    className={"chatbot-editable-action-multi-input-section"}
                    style={{
                        justifyContent: "flex-end"
                    }}
                >
                    {/* Button to add new date range */}
                    <IoAddCircle
                        size={"lg"}
                        color={"#1890ff"}
                        className="chatbot-editable-action-param-row-button"
                        onClick={() => {
                            const newRanges = [...dateRanges, [null, null]];
                            onChange(newRanges);
                        }}
                    />
                </div>
            </div>
        </div>
    )
}


////////////////
// OTHER TYPES
////////////////

export const BooleanInput = ({label, value, onChange, parameterConfig}) => {
    return (
        <div className="chatbot-editable-action-param-row">
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <Checkbox
                className="chatbot-editable-action-param-value-input param-boolean-input"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
            />
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
        </div>
    )
}

export const IntegerInput = ({label, value, onChange, parameterConfig}) => {
    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <InputNumber
                className="chatbot-editable-action-param-value-input param-integer-input"
                defaultValue={value}
                onChange={(e) => onChange(e)}
            />
        </div>
    )
}

export const DictListInput = ({label, value, onChange, parameterConfig}) => {
    return (
        <div className="chatbot-editable-action-param-row">
            {
                parameterConfig?.tooltip_text && (
                    <Tooltip
                        title={
                            parameterConfig?.tooltip_text || ""
                        }
                        className={"chatbot-editable-action-param-tooltip"}
                    >
                        <MdInfoOutline size={18}/>
                    </Tooltip>
                )
            }
            <label className="chatbot-editable-action-param-key">{label}:</label>
            <textarea
                className="chatbot-editable-action-param-value-input"
                value={JSON.stringify(value)}
                disabled={true}
                onChange={(e) => {
                    // try {
                    //     const parsedValue = JSON.parse(e.target.value);
                    //     updateParamValue(key, parsedValue);
                    // } catch (error) {
                    //     // Invalid JSON, do not update
                    // }
                }}
                rows={2}
                style={{width: '70%', maxWidth: 400}}
            />
        </div>
    )
}
